import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { quoteCart } from '../lib/shopPricing';
import { track } from '../lib/shopApi';

// Cart lives in localStorage so a shopper can leave and come back. Shape:
// { items: [{ lineId, variantId, qty, backsetMm: number | 'unsure' | null, install: bool,
//   fromChooser: bool, chooser: {slides, material, onDoor} | null }],
//   install: { inArea, doorNotes, hubSetup } | null,
//   shippingChoice: 'standard'|'rural', payment: 'card'|'poli', contact: {...} }
// Supply & install is a tick on each line (Hugh, 22 Sep 2026), not one switch for the whole
// cart; `install` holds what the visit shares. The backset is asked in the cart, and never on
// a line that came from Find my lock (`fromChooser`): the locksmith sizes it from the door
// photos after payment.
const STORAGE_KEY = 'sd_cart_v1';
const EMPTY = { items: [], install: null, shippingChoice: 'standard', payment: 'card', contact: {} };

const CartContext = createContext(null);

// A stored cart from before the per-line tick: `install.selected` fitted every line, and the
// chooser's door lived on the install answers. Both move onto the lines.
function migrate(parsed) {
  const legacy = parsed.install || {};
  const items = (Array.isArray(parsed.items) ? parsed.items : []).map((i) =>
    i.install === undefined || i.install === null
      ? { ...i, install: !!legacy.selected, fromChooser: !!legacy.chooser, chooser: legacy.chooser || null }
      : i
  );
  // Contact details are never kept between visits (Hugh, 24 Sep 2026: the checkout came back
  // filled with an earlier name, number and email) — an old stored cart drops them too.
  return { ...EMPTY, ...parsed, items, contact: {} };
}

function load() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY;
    return migrate(JSON.parse(raw));
  } catch {
    return EMPTY;
  }
}

export function CartProvider({ children }) {
  const [cart, setCart] = useState(load);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...cart, contact: {} }));
    } catch {
      /* storage unavailable — cart still works for the session */
    }
  }, [cart]);

  const addItem = useCallback((variantId, { qty = 1, backsetMm = null, install = false, fromChooser = false, chooser = null, product } = {}) => {
    setCart((c) => {
      const same = (i) => i.variantId === variantId && (i.backsetMm || null) === (backsetMm || null) && !!i.install === !!install && !!i.fromChooser === !!fromChooser;
      const existing = c.items.find(same);
      const items = existing
        ? c.items.map((i) => (i === existing ? { ...i, qty: Math.min(10, i.qty + qty) } : i))
        : [...c.items, { lineId: `${variantId}-${Date.now()}`, variantId, qty, backsetMm, install: !!install, fromChooser: !!fromChooser, chooser: chooser || null }];
      return { ...c, items };
    });
    track('add_to_cart', { variantId, qty, install: !!install, fromChooser: !!fromChooser, product: product && product.slug });
  }, []);

  const updateQty = useCallback((lineId, qty) => {
    setCart((c) => ({
      ...c,
      items: c.items.map((i) => (i.lineId === lineId ? { ...i, qty: Math.max(1, Math.min(10, qty)) } : i)),
    }));
  }, []);

  // The line's own answers: the backset, and whether it is fitted.
  const updateLine = useCallback((lineId, patch) => {
    setCart((c) => ({ ...c, items: c.items.map((i) => (i.lineId === lineId ? { ...i, ...patch } : i)) }));
  }, []);

  const removeItem = useCallback((lineId) => {
    setCart((c) => ({ ...c, items: c.items.filter((i) => i.lineId !== lineId) }));
  }, []);

  const setInstall = useCallback((install) => setCart((c) => ({ ...c, install })), []);
  const setShippingChoice = useCallback((shippingChoice) => setCart((c) => ({ ...c, shippingChoice })), []);
  const setPayment = useCallback((payment) => setCart((c) => ({ ...c, payment })), []);
  const setContact = useCallback((contact) => setCart((c) => ({ ...c, contact: { ...c.contact, ...contact } })), []);
  const clear = useCallback(() => setCart(EMPTY), []);

  const quote = useMemo(() => (cart.items.length ? quoteCart(cart) : null), [cart]);
  const count = useMemo(() => cart.items.reduce((n, i) => n + (i.qty || 1), 0), [cart.items]);

  const value = useMemo(
    () => ({ cart, quote, count, addItem, updateQty, updateLine, removeItem, setInstall, setShippingChoice, setPayment, setContact, clear }),
    [cart, quote, count, addItem, updateQty, updateLine, removeItem, setInstall, setShippingChoice, setPayment, setContact, clear]
  );
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside CartProvider');
  return ctx;
}
